import { AuthMethod } from '@shared/models/settings/auth-method';
import { Smstan } from '@shared/models/signrequest/authentication/smstan';
import { Idin } from '@shared/models/signrequest/authentication/idin';
import { Irma } from '@shared/models/signrequest/authentication/irma';
import { Digid } from '@shared/models/signrequest/authentication/digid';
import { Mouse } from '@shared/models/signrequest/authentication/mouse';
import { Csc } from '@shared/models/signrequest/authentication/csc';
import { AmDocumentTemplate } from '@shared/models/signrequest/authentication/document-template';
import {AmDocumentanchor} from '@shared/models/signrequest/authentication/documentanchor';
import {AuthenticationMethod} from '@shared/models/signrequest/authentication/authentication-method';
import {Certificate} from '@shared/models/signrequest/authentication/certificate';
import {CheckedID} from '@shared/models/signrequest/authentication/checkedid';
import {getAuthType} from '@shared/models/signrequest/authentication/base-authentication-method';
import {Sso} from '@shared/models/signrequest/authentication/sso';
import {ZynyoDocument} from '@shared/models/document/zynyo.document';
import {Role, SignSignatory} from '@shared/models/signrequest/sign.signatory';
import {SignDocumentRequestService} from '@routes/sign-request/services/sign-document-request.service';

export class SignrequestUtils {
    static validateEmail(email: string) {
               const re = /^(([^<>() [\]\\.,;:\s@"]+(\.[^<>() [\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    static findMethodByType(am: AuthenticationMethod, method: AuthMethod, preview = false) {
        // If its a preview method, remove _preview from the method name
        if (preview) {
            method = method.split('_')[0] as AuthMethod;
        }
        const type = method === 'document_anchors' ? 'documentanchor' : method;
        const authtype = preview ? 'PREVIEW' : 'AFTERVIEW';

        return am.type === type && getAuthType(am) === authtype;
    }

    static normalizeAuthType(type: string): AuthMethod {
        return (type === 'documentanchor' ? 'document_anchors' : type) as AuthMethod;
    }

    static setAuthMethods(am: AuthMethod): AuthenticationMethod {
        let bam: AuthenticationMethod;
        const preview = am.endsWith('_preview');

        switch (am) {
            case 'certificate':
                bam = new Certificate();
                break;
            case 'checkedid':
                bam = new CheckedID();
                break;
            case 'csc':
            case 'csc_preview': {
                bam = new Csc();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
            case 'digid':
            case 'digid_preview': {
                bam = new Digid();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
            case 'document_anchors': {
                bam = new AmDocumentanchor();
                break;
            }
            case 'documenttemplate': {
                bam = new AmDocumentTemplate();
                break;
            }
            case 'idin':
            case 'idin_preview': {
                bam = new Idin();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
            case 'irma':
            case 'irma_preview': {
                bam = new Irma();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
            case 'mouse':
            case 'mouse_preview': {
                bam = new Mouse();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
            case 'smstan':
            case 'smstan_preview': {
                bam = new Smstan();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
            case 'sso':
            case 'sso_preview': {
                bam = new Sso();
                bam.authtype = preview ? 'PREVIEW' : 'AFTERVIEW';
                break;
            }
        }

        return bam;
    }

    static prepareRestartRequest(doc: ZynyoDocument, signDocumentRequestService: SignDocumentRequestService): void {
        // 1. Snapshot the data we need BEFORE reset (plain values only)
        const snapshot = {
            separateAuditlog: doc.signRequest?.separateAuditlog ?? false,
            disableEmail:     doc.signRequest?.disableEmail ?? false,
            useTimeStamp:     doc.useTimestamp ?? false,
            description:      doc.description ?? '',
            metaData:         {} as Record<string, string>,
            signatories:      doc.signatories.map(s => ({
                name:          s.name,
                email:         s.email,
                signatoryRole: s.signatoryRole,
            }))
        };
        doc.signRequest?.signrequestMetaDataList?.forEach(md => {
            snapshot.metaData[md.key] = md.value;
        });

        // 2. Reset — now nothing is shared by reference
        signDocumentRequestService.resetStore();

        // 3. Apply snapshot to fresh request
        const request = signDocumentRequestService.signDocumentRequest;
        request.separateAuditlog = snapshot.separateAuditlog;
        request.disableEmail     = snapshot.disableEmail;
        request.useTimeStamp     = snapshot.useTimeStamp;

        signDocumentRequestService.description = snapshot.description;
        signDocumentRequestService.metadata    = snapshot.metaData;

        // 4. Bouw verse SignSignatory instanties inclusief Auth Methods
        signDocumentRequestService.signatories = snapshot.signatories.map((s, index) => {
            const signSignatory = new SignSignatory();
            signSignatory.name = s.name;
            signSignatory.email = s.email;
            signSignatory.signatoryRole = s.signatoryRole as unknown as Role;

            // Haal de originele auth methods op
            const originalAuthMethods = doc.signatories[index]?.authenticationMethods ?? [];

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            signSignatory.authenticationMethods = originalAuthMethods.map((am: any) => {
                // Bepaal de ruwe type waarde
                const rawType = am.type || am.authenticationMethods || '';

                return {
                    ...am,
                    // Zet de type om naar kleine letters voor de API (bijv. 'mouse')
                    type: rawType.toLowerCase(),
                    authtype: am.authtype || am.authType
                };
            });

            return signSignatory;
        });

        // 5. Navigate to file-upload
        signDocumentRequestService.setStepByPath('file-upload');
    }
}
